import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import {buildConversationItems, type ConversationItem} from './build-conversation-items'
import {ownerAvatarPath} from '@github-ui/paths'
import {getCurrentUserLogin} from '@github-ui/client-env'

export interface AuthorFilterItem {
  login: string
  avatarUrl: string
  count: number
}

type CurrentUser = Pick<AuthorFilterItem, 'login' | 'avatarUrl'>

export const getCurrentUser = () => {
  const login = getCurrentUserLogin()
  if (login) return {login, avatarUrl: ownerAvatarPath({owner: login})}
}

/**
 * Sorts current user to the top, then Copilot, then everyone else by count.
 */
const compareAuthors = (currentUser: CurrentUser | undefined) => (a: AuthorFilterItem, b: AuthorFilterItem) => {
  if (a.login === currentUser?.login) return -1
  if (b.login === currentUser?.login) return 1
  if (a.login === 'Copilot') return -1
  if (b.login === 'Copilot') return 1
  return b.count - a.count || a.login.localeCompare(b.login)
}

/**
 * Builds a list of unique authors from thread previews.
 * Copilot authors are placed at the top of the list.
 */
export function getThreadPreviewsAuthors(threadPreviews: Thread[]): AuthorFilterItem[] {
  return getConversationItemsAuthors(buildConversationItems([], [], threadPreviews))
}

export function getConversationItemAuthor(conversationItem: ConversationItem) {
  switch (conversationItem.type) {
    case 'issue-comment':
      return {login: conversationItem.data.authorLogin, avatarUrl: conversationItem.data.authorAvatarUrl ?? ''}
    case 'review-summary':
      return {
        login: conversationItem.data.summary.authorLogin,
        avatarUrl: conversationItem.data.summary.authorAvatarUrl ?? '',
      }
    case 'thread':
      return getFirstComment(conversationItem.data)?.author
  }
}

/**
 * Builds a list of unique authors from issue comments.
 * Copilot authors are placed at the top of the list.
 */
export function getConversationItemsAuthors(items: ConversationItem[]): AuthorFilterItem[] {
  const currentUser = getCurrentUser()

  // Start with current user so they are always present in the list
  const authors = new Map<string, AuthorFilterItem>(
    currentUser ? [[currentUser.login, {...currentUser, count: 0}]] : [],
  )

  for (const item of items) {
    const author = getConversationItemAuthor(item)
    if (!author) continue

    const entry = authors.get(author.login) ?? {login: author.login, avatarUrl: author.avatarUrl ?? '', count: 0}
    entry.count++
    authors.set(author.login, entry)
  }

  return Array.from(authors.values()).sort(compareAuthors(currentUser))
}
